/**
 * plansCatalog — single source of truth for the Notches research-plan
 * catalog used by /n2/plans and the Evaluate bundle overviews at /n2.
 *
 * Keep names, prices, coverage, and purchase status here so Evaluate and
 * /n2/plans stay consistent. Automated-trader plans and the Boring Bucket
 * mentored membership live elsewhere and are not modeled here.
 *
 * Rules the catalog respects:
 *   - Premier, SPX Only, ES Only, Index & ETF Research, Futures Options
 *     Research, and All Research are wired to live checkout. The backend
 *     Lambda still guards each plan via `paymentProviderConfigured` in
 *     productConfig.js (derived from Stripe Price IDs on the deployed
 *     Lambda's env vars); the frontend `purchase: 'ready'` flag only
 *     tells the UI to offer the standard purchase affordance.
 *   - The Boring Bucket is $299/month only publicly. Its annual SKU
 *     remains configured elsewhere but is not promoted here.
 *   - Coverage per plan is fixed. New markets and products are NOT
 *     automatically added to existing plans.
 */

export type PlanKey =
  | 'premier'
  | 'spx-only'
  | 'es-only'
  | 'index-etf-research'
  | 'futures-options-research'
  | 'all-research'
  | 'boring-bucket-mentored';

/**
 * Standalone specialist plans — the single-market exceptions that sit
 * alongside the bundles. Callers use this set to render the "specialist
 * options" grouping on /n2/plans and the per-bundle secondary callout.
 */
export const SPECIALIST_PLAN_KEYS: readonly PlanKey[] = ['spx-only', 'es-only'];

export type PurchaseStatus = 'ready' | 'pending' | 'application';

export type ResearchPlan = {
  key: PlanKey;
  name: string;
  /** Short one-line label of what's covered (used in the Plans card row). */
  coverage: string;
  /** Included-market chips shown on bundle overviews. Empty for Premier. */
  markets: string[];
  monthlyPrice: number;
  /** Null renders as monthly-only (Boring Bucket). */
  yearlyPrice: number | null;
  purchase: PurchaseStatus;
  /** One-sentence benefit statement. */
  description: string;
  /** Verified benefits shown below coverage. Keep short and factual. */
  bullets: string[];
};

export const RESEARCH_PLANS: ResearchPlan[] = [
  {
    key: 'premier',
    name: 'Premier',
    coverage: 'Stocks, equity options, reporting',
    markets: [],
    monthlyPrice: 29.99,
    yearlyPrice: 299.99,
    purchase: 'ready',
    description: 'Research for individual-stock and equity-options traders.',
    bullets: [
      'Researched opportunities on individual companies and equity-options structures (spreads, covered calls, naked puts).',
      'Each opportunity carries supporting analysis, risk considerations, and — where applicable — a proposed structure and range.',
      'Accessed through the Notches research dashboard.',
    ],
  },
  {
    key: 'spx-only',
    name: 'SPX Only',
    coverage: 'SPX',
    markets: ['SPX'],
    monthlyPrice: 79.99,
    yearlyPrice: 799.99,
    purchase: 'ready',
    description: 'Research for SPX index-options traders.',
    bullets: [
      'Researched opportunities on SPX options, including 0DTE credit spreads and multi-day structures.',
      'Each opportunity carries supporting analysis, risk considerations, and an analytical rating.',
      'Accessed through the Notches research dashboard.',
    ],
  },
  {
    key: 'es-only',
    name: 'ES Only',
    coverage: 'ES',
    markets: ['ES'],
    monthlyPrice: 79.99,
    yearlyPrice: 799.99,
    purchase: 'ready',
    description: 'Research for E-mini S&P 500 futures-options traders.',
    bullets: [
      'Researched opportunities on ES futures options across weekly and quarterly series.',
      'Each opportunity carries supporting analysis, risk considerations, and an analytical rating.',
      'Extended-hours snapshots on the ES chain.',
      'Accessed through the Notches research dashboard.',
    ],
  },
  {
    key: 'index-etf-research',
    name: 'Index & ETF Research',
    coverage: 'SPX, SPY, QQQ, IWM',
    markets: ['SPX', 'SPY', 'QQQ', 'IWM'],
    monthlyPrice: 99.99,
    yearlyPrice: 999.99,
    purchase: 'ready',
    description: 'Researched opportunities across major index and ETF options.',
    bullets: [
      'Coverage on SPX, SPY, QQQ, and IWM options in one subscription.',
      'Each opportunity carries supporting analysis and risk considerations.',
      'Accessed through the Notches research dashboard.',
    ],
  },
  {
    key: 'futures-options-research',
    name: 'Futures Options Research',
    coverage: 'ES, MES, GC, CL',
    markets: ['ES', 'MES', 'GC', 'CL'],
    monthlyPrice: 99.99,
    yearlyPrice: 999.99,
    purchase: 'ready',
    description:
      'Researched opportunities across equity-index, gold, and crude-oil futures options.',
    bullets: [
      'Coverage on ES, MES, gold, and crude-oil futures options in one subscription.',
      'Each opportunity carries supporting analysis and risk considerations.',
      'Accessed through the Notches research dashboard.',
    ],
  },
  {
    key: 'all-research',
    name: 'All Research',
    coverage:
      'Premier plus the listed index, ETF, and futures-options coverage',
    markets: ['Premier', 'SPX', 'SPY', 'QQQ', 'IWM', 'ES', 'MES', 'GC', 'CL'],
    monthlyPrice: 199.99,
    yearlyPrice: 1999.99,
    purchase: 'ready',
    description: 'A single subscription across the current research catalog.',
    bullets: [
      'Premier — stocks, equity options, reporting.',
      'Index & ETF Research — SPX, SPY, QQQ, IWM.',
      'Futures Options Research — ES, MES, GC, CL.',
      'Accessed through the Notches research dashboard.',
    ],
  },
];

export const BORING_BUCKET_PLAN: ResearchPlan = {
  key: 'boring-bucket-mentored',
  name: 'The Boring Bucket',
  coverage: 'All Research plus a mentored membership',
  markets: [],
  monthlyPrice: 299.0,
  yearlyPrice: null,
  purchase: 'application',
  description: 'Learn to apply the framework — with guidance.',
  bullets: [
    'Everything in All Research.',
    "Founder's live-account view — a study aid, not a signal to copy.",
    'Group mentoring sessions with the founder.',
    'Session recordings so members can catch up on their own time.',
    'Capital allocation framework — position sizing, buying-power, profit handling.',
    'Member community for framework discussion.',
    'Replaces your research membership; it is not an additional charge on top.',
  ],
};

/** Look up a plan by key. */
export function getPlan(key: PlanKey): ResearchPlan {
  if (key === 'boring-bucket-mentored') return BORING_BUCKET_PLAN;
  const plan = RESEARCH_PLANS.find((p) => p.key === key);
  if (!plan) throw new Error(`Unknown plan key: ${key}`);
  return plan;
}

/**
 * Deep-link into the public /n2/plans comparison. The Plans page uses
 * plan keys as anchor ids so bundle overviews can send visitors to the
 * specific plan section instead of the top of the page.
 */
export function plansPagePath(key: PlanKey): string {
  return `/n2/plans#${key}`;
}
